'use client';

/**
 * The slot's client-side measurement (ads build plan sections 5.1 and 5.4):
 * the impression beacon, the viewable beacon, and the browser-direct pixels.
 *
 * IMPRESSION: a beacon once the ad has actually been rendered AND a person
 * could see it, because the MRC counts impressions client-side only ("Client
 * side measurement is the only acceptable method", research [MOBW]).
 * "Rendered" is the first report of an IntersectionObserver on the drawn ad:
 * that report is delivered in the browser's rendering step, after the element
 * was laid out. "Could see it" is `adSeeable` below: the document visible,
 * the window focused, and the ad UNCOVERED. Checked at that first report and
 * then on every 100 ms sample until it holds, so an ad drawn under a dialog
 * counts its impression when the dialog closes, never before. Once per token.
 *
 * VIEWABLE, the MRC rule on the AD ITSELF (not the slot around it): at least
 * 50% of its pixels intersecting, and `adSeeable` (the document visible, the
 * window focused, the ad uncovered), for one continuous second, sampled every
 * 100 ms (the MRC asks for 100 ms or less, research [MRCV]). A sample that
 * fails resets the second; a timer that was suspended (a background tab
 * thawing) cannot credit the gap. Then one `sendBeacon` to `/api/ads/beacon`.
 *
 * UNCOVERED, DEFENCE IN DEPTH (2026-09-30 fix round; the visual pass found an
 * ad under the Admin dialog with `data-ad-state` "shown", counted as served
 * and possibly as an impression). The workspace already keeps the slot
 * inactive when a dialog, Discover, Stage or another window covers it (only
 * the top window's slot is active at all, lane A; node-frame.tsx asks
 * `useCanvasCovered` too). This is the second line, and it holds for every
 * placement (title bar, app bar, full screen, the app's own address) whatever
 * the chrome around it forgets: `document.elementFromPoint` at the ad's
 * centre and at two inner points on its middle line must land INSIDE the ad
 * element. An IntersectionObserver cannot see occlusion (it knows nothing of
 * a dialog, a popover or the top layer), hit testing can. Known limit: an
 * overlay with `pointer-events: none` is invisible to hit testing (the app
 * switcher's HUD strip, for its 1.4 s); anything a person can click on is not.
 * A browser without `elementFromPoint` counts nothing.
 *
 * BROWSER-DIRECT PIXELS (EthicalAds' view URL, BuySellAds' `statimp`): the
 * impression route hands them back only after it has counted the impression,
 * so they can never fire before our own count. Each is an image request with
 * `crossOrigin = 'anonymous'` (so the browser sends no cookies to the
 * network) and `referrerPolicy = 'no-referrer'` (so the page's address is not
 * sent either). Never `fetch` (the site's `connect-src` would refuse it and
 * should), never a script, never attached to the document. Under COEP a
 * response without CORS headers is discarded, which is fine: the request has
 * already reached the network, and nothing is ever read back.
 *
 * The browser otherwise only ever calls our own routes.
 */

import { useEffect, type RefObject } from 'react';

import { registrableDomain } from '@/lib/auth/registrable-domain';

export const VIEWABLE_MS = 1000;
export const SAMPLE_MS = 100;
export const VIEWABLE_RATIO = 0.5;
/**
 * Where the hit test samples the ad, as fractions of its width on its middle
 * line: the centre first, then two inner points. The ad is one line of text,
 * so the middle line crosses the chip, the advertiser, the headline and (i).
 */
export const UNCOVERED_SAMPLE_POINTS = [0.5, 0.2, 0.8] as const;
/** The most pixels one impression may fire; the server caps its list at the same number. */
export const MAX_PIXELS = 5;

const impressionsSent = new Set<string>();
const viewablesSent = new Set<string>();
/** A reference to each pixel until it settles, so a collected Image cannot cancel its own request. */
const pixelsInFlight = new Set<HTMLImageElement>();

/** Test hook: forget every beacon sent and every pixel in flight. */
export function _resetAdTrackersForTests(): void {
  impressionsSent.clear();
  viewablesSent.clear();
  pixelsInFlight.clear();
}

function remember(set: Set<string>, token: string): void {
  set.add(token);
  if (set.size > 500) {
    const oldest = set.values().next().value;
    if (oldest) set.delete(oldest);
  }
}

/**
 * An https URL on ANOTHER site. A same-site pixel is refused: `crossOrigin =
 * 'anonymous'` only drops cookies across origins, so a network that named our
 * own address would otherwise send the viewer's cookies to our own API (the
 * server refuses such candidates too, lib/ads/own-hosts.ts).
 */
function isHttpsUrl(value: unknown): value is string {
  if (typeof value !== 'string' || value.length > 2048) return false;
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:' || url.username || url.password) return false;
    if (typeof location !== 'undefined' && location.hostname) {
      if (url.origin === location.origin) return false;
      if (registrableDomain(url.hostname) === registrableDomain(location.hostname)) return false;
    }
    return true;
  } catch {
    return false;
  }
}

/** Fire one browser-direct tracker as a cookieless, referrer-less image. False when refused. */
export function firePixel(url: unknown): boolean {
  if (typeof Image === 'undefined' || !isHttpsUrl(url)) return false;
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.referrerPolicy = 'no-referrer';
  img.decoding = 'async';
  const settle = () => {
    img.onload = null;
    img.onerror = null;
    pixelsInFlight.delete(img);
  };
  img.onload = settle;
  img.onerror = settle;
  pixelsInFlight.add(img);
  img.src = url;
  return true;
}

/** The pixels in an impression answer, or none. */
export function pixelsFrom(body: unknown): string[] {
  if (typeof body !== 'object' || body === null) return [];
  const pixels = (body as { pixels?: unknown }).pixels;
  return Array.isArray(pixels) ? pixels.filter(isHttpsUrl).slice(0, MAX_PIXELS) : [];
}

/** The impression beacon, once per token; then whatever pixels our server hands back. */
export function sendImpression(token: string): void {
  if (impressionsSent.has(token)) return;
  remember(impressionsSent, token);
  void fetch('/api/ads/impression', {
    method: 'POST',
    credentials: 'same-origin',
    keepalive: true,
    headers: { 'content-type': 'text/plain' },
    body: token,
  })
    .then(async (res) => (res.status === 200 ? ((await res.json()) as unknown) : null))
    .then((body) => {
      for (const url of pixelsFrom(body)) firePixel(url);
    })
    .catch(() => {});
}

/**
 * The ad is not covered: the hit test at every sample point lands on the ad
 * element or inside it. False for an ad with no box (not laid out), for a
 * point outside the viewport (`elementFromPoint` answers null there), and
 * where the browser has no `elementFromPoint` at all, so a doubt never counts.
 */
export function adUncovered(el: Element): boolean {
  if (typeof document === 'undefined' || typeof document.elementFromPoint !== 'function') return false;
  const r = el.getBoundingClientRect();
  if (!(r.width > 0) || !(r.height > 0)) return false;
  const y = r.top + r.height / 2;
  for (const f of UNCOVERED_SAMPLE_POINTS) {
    const hit = document.elementFromPoint(r.left + r.width * f, y);
    if (!hit || (hit !== el && !el.contains(hit))) return false;
  }
  return true;
}

/** A person could see the ad now: the document visible, the window focused, and the ad uncovered. */
export function adSeeable(el: Element): boolean {
  if (typeof document === 'undefined') return false;
  return document.visibilityState === 'visible' && document.hasFocus() && adUncovered(el);
}

/** The viewable beacon, once per token (lane A's `/api/ads/beacon`, which reads a plain-text token). */
export function sendViewable(token: string): void {
  if (viewablesSent.has(token)) return;
  remember(viewablesSent, token);
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function' && navigator.sendBeacon('/api/ads/beacon', token)) {
      return;
    }
  } catch {
    /* fall through */
  }
  void fetch('/api/ads/beacon', {
    method: 'POST',
    credentials: 'same-origin',
    keepalive: true,
    headers: { 'content-type': 'text/plain' },
    body: token,
  }).catch(() => {});
}

export interface AdTrackerOptions {
  /** The served ad's token; null draws nothing to measure. */
  token: string | null;
  /** The drawn ad (the chip, advertiser and headline), not the slot region around it. */
  elementRef: RefObject<HTMLElement | null>;
  /** The ad is drawn, fits, and is not a preview. */
  active: boolean;
}

/**
 * Measure one drawn ad: the impression once it has rendered and a person
 * could see it, the viewable once the MRC rule holds on an uncovered ad.
 */
export function useAdTrackers({ token, elementRef, active }: AdTrackerOptions): void {
  useEffect(() => {
    if (!active || !token) return;
    const el = elementRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    if (impressionsSent.has(token) && viewablesSent.has(token)) return;

    let ratio = 0;
    let rendered = false;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) ratio = entry.isIntersecting ? entry.intersectionRatio : 0;
        rendered = true;
        // The first chance to count: an ad drawn in view, uncovered, on a
        // visible and focused page counts at render, as before. Otherwise the
        // sampler below keeps asking.
        if (!impressionsSent.has(token) && adSeeable(el)) sendImpression(token);
      },
      { threshold: [0, VIEWABLE_RATIO, 1] },
    );
    io.observe(el);

    // One sampler for both events. The cheap gates run first; the hit test
    // (`adSeeable`) only once the ad has rendered, and only while its answer
    // can still count something (an impression is owed, or half the ad is in
    // view for the viewable second).
    let accrued = 0;
    let last = Date.now();
    let timer: ReturnType<typeof setInterval> | undefined = setInterval(() => {
      const now = Date.now();
      const delta = Math.min(now - last, SAMPLE_MS * 2);
      last = now;
      const needImpression = !impressionsSent.has(token);
      const needViewable = !viewablesSent.has(token);
      if (!needImpression && !needViewable) {
        if (timer) clearInterval(timer);
        timer = undefined;
        return;
      }
      const seen = rendered && (needImpression || ratio >= VIEWABLE_RATIO) && adSeeable(el);
      if (seen && needImpression) sendImpression(token);
      if (!needViewable) return;
      accrued = seen && ratio >= VIEWABLE_RATIO ? accrued + delta : 0;
      if (accrued >= VIEWABLE_MS) {
        // A second of seen samples began with one that sent the impression.
        sendViewable(token);
        if (timer) clearInterval(timer);
        timer = undefined;
      }
    }, SAMPLE_MS);
    return () => {
      if (timer) clearInterval(timer);
      io.disconnect();
    };
  }, [active, token, elementRef]);
}
